import CustomTooltip from '@UI/tooltip/CustomTooltip';
import type { NamedSetHref } from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import { selectDocumentRootItem } from '@newStore/documentApi/documentApiSelectors';
import { selectIsRoot } from '@newStore/documentUI/documentUISelectors';
import { getAllOfResource } from '@newStore/externalData/externalDataState';
import { pathMap } from '@newStore/externalData/externalDataTypes';
import type { EditAsideNamedSet } from '@nodeTypeConfig/configTypes';
import { patchNodeAction, resetNamedSetsAction } from '@store/actions/documentActions';
import { getResourceKey } from '@store/helpers/documentHelpers';
import { selectCurrentOrInheritedPropertyValue } from '@store/selectors/asideSelectors';
import { set } from 'lodash';
import { any } from 'prop-types';
import type React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEditHref } from '../../../hooks/UseEditHref';
import { useInitialValue } from '../../../hooks/UseInitialValue';

import './AsideNamedSet.scss';
import { selectIsReadOnly, selectNamedSetOptionsForTag } from './asideNamedSetSelectors';

const AsideNamedSet: React.FC<{
  config: EditAsideNamedSet;
  readonly: boolean;
}> = ({ config, readonly }) => {
  const dispatch = useDispatch();
  const editHref = useEditHref();
  const readOnly = useSelector(
    (state: RootState) => selectIsReadOnly(state, config.property) || readonly,
  );
  const inheritedPropertyValue: NamedSetHref[] = useSelector((state: RootState) =>
    selectCurrentOrInheritedPropertyValue(state, editHref, config),
  );

  const itemValue: NamedSetHref[] | undefined = useInitialValue(config.property);

  const hasRootNamedSets = useSelector((state: RootState) =>
    Boolean(selectDocumentRootItem(state)?.[config.property]?.length),
  );
  const isRoot = useSelector((state: RootState) => selectIsRoot(state, editHref));

  const namedSetOptions = useSelector((state: RootState) =>
    selectNamedSetOptionsForTag(state, editHref, config),
  );

  const isInherited = !isRoot && !itemValue;

  useEffect(() => {
    if (config.tag) {
      dispatch(getAllOfResource({ resource: pathMap.namedSets, refresh: false }));
    }
  }, [dispatch, config.tag]);

  const change = (value: boolean, key: string) => {
    const currentSelectedNamedSets = inheritedPropertyValue;
    const updatedSelectedNamedSets = value
      ? [...currentSelectedNamedSets, key].filter(
          (item, index, self) => self.indexOf(item) === index,
        )
      : currentSelectedNamedSets.filter((item) => item !== key);

    dispatch(
      patchNodeAction(getResourceKey(editHref), set({}, config.property, updatedSelectedNamedSets)),
    );
  };

  const clearAll = () => {
    dispatch(patchNodeAction(getResourceKey(editHref), set({}, config.property, [])));
  };

  const selectAll = () => {
    const allHrefs = namedSetOptions.map((option) => option.href);
    const updatedSelectedNamedSets = [...new Set([...inheritedPropertyValue, ...allHrefs])];

    dispatch(
      patchNodeAction(getResourceKey(editHref), set({}, config.property, updatedSelectedNamedSets)),
    );
  };

  const backToInherited = () => {
    dispatch(resetNamedSetsAction(config.property, getResourceKey(editHref)));
  };

  return (
    <div className={`namedSet${isInherited ? ' inherited' : ''}`}>
      {!isRoot && !hasRootNamedSets && (
        <>
          <label className="control-label">{`${config.label} ${isRoot ? '*' : ''}`}</label>
          <div>
            <label className="warning-label">
              {`De root heeft geen ${config.label} gedefinieerd. Voeg het eerst toe aan het hoofdniveau.`}
            </label>
          </div>
        </>
      )}

      {(isRoot || hasRootNamedSets) && (
        <>
          <div>
            <label className="control-label">{`${config.label} ${isRoot ? '*' : ''}`}</label>
            {isInherited && <span className="inherited-info">Overgenomen</span>}
            {!isInherited && !isRoot && (
              <span className="inherited-revert" onClick={backToInherited}>
                Overnemen
              </span>
            )}
          </div>
          {config.subLabel && (
            <div>
              <label className="subtitle-label">{config.subLabel}</label>
            </div>
          )}
        </>
      )}

      <div className="row" style={{ marginLeft: '15px', marginRight: '15px' }}>
        <div className="col-md-6">
          {namedSetOptions.map((option) => (
            <div className="checkbox-row" key={option.key}>
              <input
                id={option.key}
                type="checkbox"
                checked={option.isChecked}
                onChange={(input) => change(input.target.checked, option.href)}
                disabled={readOnly}
              />
              <label
                htmlFor={option.key}
                className={`option${readOnly ? ' aside-component-disabled' : ''}`}
              >
                {option.name}
                {option.tooltip && (
                  <CustomTooltip name={option.tooltip} className="inline-tooltip">
                    <sup>
                      <span className="glyphicon glyphicon-question-sign" />
                    </sup>
                  </CustomTooltip>
                )}
              </label>
            </div>
          ))}
        </div>

        {!readOnly && (
          <div className="col-md-12 named-set-actions">
            <a onClick={clearAll}>Alles wissen</a>
            <a onClick={selectAll} style={{ marginLeft: '10px' }}>
              Alles selecteren
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default AsideNamedSet;

AsideNamedSet.displayName = 'AsideNamedSet';

AsideNamedSet.propTypes = {
  config: any,
  readonly: any,
};
